import { action, makeObservable, observable, runInAction } from 'mobx';

import { FirebaseApp } from 'firebase/app';
import {
    Auth,
    getAuth,
    GoogleAuthProvider,
    OAuthProvider,
    onAuthStateChanged,
    signInWithPopup,
    signInWithRedirect,
    signInWithCustomToken,
} from 'firebase/auth';
import _ from 'lodash';
import { apiClient } from '@app/apiClient';
import { FirebaseAuthStore, TokenUserData } from './firebaseStore';
import modalStore from './modalStore';
import { SignInProvider } from '@type/authType';

/**
 * 모바일 리다이렉트 로그인 진행 중임을 나타내는 sessionStorage 키
 * - signInWithRedirect 호출 전에 저장, onAuthStateChanged 에서 소비함
 */
const PENDING_REDIRECT_LOGIN_KEY = 'pendingRedirectLogin';

export class FirebaseWebAuthStore implements FirebaseAuthStore {
    readonly firebaseAuth: Auth;

    /**
     * 로그인 진행 중 상태
     */
    isLoggingIn = false;

    loginMethod: SignInProvider | null = null;

    me: TokenUserData | null = null;

    constructor(firebaseApp: FirebaseApp, authReadyResolve: () => void) {
        makeObservable<FirebaseWebAuthStore, 'loginWithProvider'>(this, {
            isLoggingIn: observable,
            loginMethod: observable,
            me: observable,

            loginWithGoogle: action,
            loginWithApple: action,
            loginWithProvider: action,
            loginWithCode: action,
            getIdToken: action,
            updateMe: action,
            refreshToken: action,
            logout: action,
        });

        const firebaseAuth = getAuth(firebaseApp);

        this.firebaseAuth = firebaseAuth;

        (async () => {
            /*
             * Firebase 인증은 로그인하면 자체적으로 토큰을 저장함 (IndexedDB)
             * 토큰 갱신도 자동으로 되기 때문에 (getIdToken() 호출할 때 자동으로 됨) 별도로 관리할 필요 없음
             *
             * 단, API 요청 시에 토큰을 전달하는 것은 직접 처리해야 함 (apiClient 에서 추가함)
             * 첫 페이지나 SSR 환경에서는 전달하려면 별도 처리해야 하므로 과감하게 생략함
             *
             * 아래 핸들러는 토큰과 관련된 상태 변경을 감지함
             * - 사용자가 로그인할 때 (새로운 ID Token 획득).
             * - 사용자가 로그아웃할 때 (ID Token 제거).
             * - 앱이 처음 로드될 때, 그리고 이전에 로그인한 사용자의 세션이 브라우저에 저장되어 있을 경우 (저장된 ID Token 자동 로드).
             * - Firebase ID Token이 자동으로 갱신될 때.
             *
             * 단, signInWithPopup() 으로 중복 로그인 하거나, getIdToken(true) 로 명시적으로 토큰을 갱신한 경우에도 호출되지 않음
             * 모든 액션을 여기에 의존할 수는 없어서, 명시적으로 갱신해야 할 땐 refreshToken() 을 호출해야 함
             */
            onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
                /*
                 * 모바일 리다이렉트 로그인 후 복귀 처리
                 *
                 * getRedirectResult() 는 Firebase 가 authDomain iframe 을 통해 결과를 전달하는데,
                 * 브라우저의 서드파티 스토리지 차단 정책에 걸리면 null 을 반환하는 경우가 있음
                 * Firebase 는 IndexedDB 에 토큰을 직접 저장하므로 onAuthStateChanged 는 정상 발화함
                 * 따라서 리다이렉트 전에 sessionStorage 에 플래그를 저장하고 여기서 감지해 처리함
                 */
                const pendingLoginMethod = sessionStorage.getItem(PENDING_REDIRECT_LOGIN_KEY) as SignInProvider | null;

                if (pendingLoginMethod && firebaseUser) {
                    sessionStorage.removeItem(PENDING_REDIRECT_LOGIN_KEY);

                    runInAction(() => {
                        this.isLoggingIn = true;
                        this.loginMethod = pendingLoginMethod;
                    });

                    try {
                        const idToken = await firebaseUser.getIdToken();
                        const user = await apiClient.firebase.public.loginWithGoogleOrApple.mutate({ idToken });
                        if (user) {
                            await this.refreshToken();
                        } else {
                            throw new Error('login failed');
                        }
                    } catch (e) {
                        console.error(e);
                        await modalStore.alert({
                            title: '로그인 실패',
                            message: '로그인 실패했습니다. 다시 시도해주세요.',
                        });
                    } finally {
                        runInAction(() => {
                            this.isLoggingIn = false;
                            this.loginMethod = null;
                        });
                        _.delay(() => {
                            authReadyResolve();
                        }, 100);
                    }
                    return;
                }

                // setCustomUserClaims() 으로 별도로 지정한 사용자 정보는 getIdTokenResult() 로 가져와야 함
                const idTokenResult = await firebaseAuth.currentUser?.getIdTokenResult();

                runInAction(() => {
                    this.me = idTokenResult?.claims?._user as TokenUserData | null;

                    // 초기화는 UI 처리를 위해 지연 처리
                    _.delay(() => {
                        authReadyResolve();
                    }, 100);
                });
            });
        })();
    }

    async loginWithGoogle() {
        await this.loginWithProvider('google', new GoogleAuthProvider());
    }

    async loginWithApple() {
        await this.loginWithProvider('apple', new OAuthProvider('apple.com'));
    }

    private async loginWithProvider(
        loginMethod: 'google' | 'apple',
        provider: GoogleAuthProvider | OAuthProvider,
    ) {
        if (this.isLoggingIn) {
            return;
        }

        // Android Chrome은 signInWithPopup 시 팝업을 Custom Tab으로 열어 sessionStorage state가 소실됨
        // 모바일에서는 페이지 이동 방식인 signInWithRedirect 를 사용하고, 결과는 onAuthStateChanged 에서 처리함
        const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
        if (isMobile) {
            sessionStorage.setItem(PENDING_REDIRECT_LOGIN_KEY, loginMethod);
            await signInWithRedirect(this.firebaseAuth!, provider);
            return;
        }

        this.loginMethod = loginMethod;
        this.isLoggingIn = true;

        try {
            const result = await signInWithPopup(this.firebaseAuth!, provider);

            // Firebase SDK 에서 자동으로 토큰을 브라우저에 저장하기 때문에 별도로 저장 처리하지 않아도 됨
            // 참고로 Firebase 는 토큰 발급과 갱신을 포함한 모든 인증 과정을 브라우저에서 진행함
            // 서버에서는 토큰을 검증하는 것만 처리해주면 됨
            const idToken = await result.user.getIdToken();

            const user = await apiClient.firebase.public.loginWithGoogleOrApple.mutate({ idToken });

            if (user) {
                // 로그인 후엔 서비스 정보도 업데이트 되었을 수 있으므로 토큰을 갱신해준다
                await this.refreshToken();
            } else {
                throw new Error('login failed');
            }
        } catch (e) {
            console.error(e);

            await modalStore.alert({
                title: '로그인 실패',
                message: '로그인 실패했습니다. 다시 시도해주세요.',
            });
        } finally {
            runInAction(() => {
                this.isLoggingIn = false;
                this.loginMethod = null;
            });
        }
    }

    async loginWithCode(code: string) {
        if (this.isLoggingIn) {
            return;
        }

        this.loginMethod = 'code';
        this.isLoggingIn = true;

        try {
            // 서버에서 코드 검증 후 Custom Token 획득
            const result = await apiClient.firebase.public.loginWithCode.mutate({ code });

            if (result.customToken && result.user) {
                // Custom Token으로 Firebase 로그인
                await signInWithCustomToken(this.firebaseAuth!, result.customToken);

                // 로그인 후 토큰 갱신
                await this.refreshToken();
            } else {
                throw new Error('Code login failed');
            }
        } catch (e) {
            console.error('loginWithCode error', e);

            await modalStore.alert({
                title: '로그인 실패',
                message: '잘못된 코드입니다. 다시 시도해주세요.',
            });
        } finally {
            runInAction(() => {
                this.isLoggingIn = false;
                this.loginMethod = null;
            });
        }
    }

    async getIdToken(): Promise<string | null> {
        return this.firebaseAuth!.currentUser?.getIdToken() || null;
    }

    async updateMe() {
        const idTokenResult = await this.firebaseAuth!.currentUser?.getIdTokenResult();
        runInAction(() => {
            this.me = idTokenResult?.claims?._user as TokenUserData | null;
        });
    }

    async refreshToken() {
        // 토큰 갱신은 파라미터로 true 를 전달해주면 됨
        const idTokenResult = await this.firebaseAuth!.currentUser?.getIdTokenResult(true);
        runInAction(() => {
            this.me = idTokenResult?.claims?._user as TokenUserData | null;
        });
    }

    async saveFcmTokenToServer() {
        // 웹뷰에서 처리하므로 아무것도 하지 않음
    }

    async logout() {
        await this.firebaseAuth!.signOut();
        runInAction(() => {
            this.me = null;
        });
    }
}
